import { determineHashAlgorithm, determineSignAlgorithm } from '@/sign/helpers/sign.helper';
import { SignPrivateKeyStrategy } from './sign-private-key.strategy';
import { SigningParams } from '@/sign/models/signing-params.model';
import { AppSignKeyOperationType, SignMethod, SignPurpose } from '@/sign/models/sign-constants';
import { depositSignService } from '@/services/deposit-sign.service';
import { MixWithOwnCertFromSigningParams } from '@/sign/mixins/deposit-sign.mixin';
import { MixWithDiiaSigning } from '@/sign/mixins/diia-sign.mixin';
import store from '@/store/store';
import { actions as _act } from '@/models/store.actions';
import { disableShowMsg, enableShowMsg, setLoadingCancelCallback, setLoadingText } from '@/mixins/ui-mixin';
import { getLocale } from '@/helpers';
import { cloudKeySignService } from '@/services/cloud-key-sign.service';
import { MixWithCloudKeyFileSystemKeyType } from '../mixins/cloud-key-sign.mixin';
import { MixWithFillEndUserOwnerInfoFromSigns } from '../mixins/general-sign.mixin';
import { EventBus } from '@/helpers/event-bus';
import { executeCloudSignOperationWithConfirmationByQrCode } from '../helpers/cloud-sign-qr.helper';
import { isCancelledError } from '@/helpers/error-helper';
import { getRecaptchaTokenIfNotAuthenticated } from '@/helpers/recaptha-helper';

abstract class SignTextPrivateKeyStrategy extends SignPrivateKeyStrategy {
  private signText: string;

  constructor(prms: any) {
    super();
    this.signText = prms?.signText || '';
    this.signPurpose = prms?.signPurpose || SignPurpose.None;
  }

  public async execute(prms: SigningParams): Promise<any> {
    disableShowMsg();
    try {
      await super.execute(prms);

      await this.init();

      await this.determineKeyTypes();

      const hashAlgo = determineHashAlgorithm(this.keyType);

      let hashBase64 = await this.euSignLib.HashData(hashAlgo, this.signText, true);

      const signAlgo = determineSignAlgorithm(this.keyType);

      const sign = await this.signHash(signAlgo, hashBase64, true, true);

      return { sign, info: this.signingParams.data?.endUserOwnerInfo };
    } finally {
      enableShowMsg();
    }
  }

  protected async signHash(
    signAlgo: number, hash: string, appendCert: boolean, asBase64String?: boolean): Promise<any> {
    return this.euSignLib.CtxSignHash(this.privateKeyContext, signAlgo, hash, appendCert, asBase64String);
  }
}

export class SignTextFileSystemStrategy extends SignTextPrivateKeyStrategy {
  protected signMethod?: SignMethod = SignMethod.FileSystem;
}

export class SignTextDeviceStrategy extends SignTextPrivateKeyStrategy {
  protected signMethod?: SignMethod = SignMethod.Device;
}

export class SignTextCloudStrategy extends SignTextPrivateKeyStrategy {
  protected signMethod?: SignMethod = SignMethod.Cloud;

  constructor(prms: any) {
    super(prms);

    this.mobileAppName = prms?.mobileAppName;
  }
}

export class SignTextDepositSignLoginPswStrategy
  extends MixWithOwnCertFromSigningParams(SignTextCloudStrategy) {
  protected async signHash(
    signAlgo: number, hash: string, appendCert: boolean, asBase64String?: boolean): Promise<any> {
    store.commit(_act.SETTTEMP_MUT_ISSHOWMSG, false);
    try {
      let { depositSignUserSessionId, keyId, keyPassword } = this.signingParams.data;

      let { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();

      const signs = await depositSignService.signHashes(
        depositSignUserSessionId,
        keyId,
        keyPassword,
        [{ id: '', data: hash }],
        recaptchaToken,
        recaptchaType);

      return signs && signs.length > 0 ? signs[0].sign : '';
    } finally {
      store.commit(_act.SETTTEMP_MUT_ISSHOWMSG, true);
    }
  }
}

export class SignTextDiiaStrategy
  extends MixWithFillEndUserOwnerInfoFromSigns(MixWithDiiaSigning(SignTextCloudStrategy)) {
  protected async signHash(
    signAlgo: number, hash: string, appendCert: boolean, asBase64String?: boolean): Promise<any> {
    const hashes = [
      {
        fileName: getLocale(`dictionary.signPurpose.${SignPurpose[this.signPurpose]}`),
        fileHash: hash
      }
    ];

    let signs = await this.signFileHashesWithDiia(hashes);

    await this.fillEndUserOwnerInfoFromSigns(signs);

    return signs && signs.length > 0 ? signs[0].signature : '';
  }
}

export class SignTextCloudKeyFileSystemStrategy
  extends MixWithFillEndUserOwnerInfoFromSigns(MixWithCloudKeyFileSystemKeyType(SignTextCloudStrategy)) {
  protected async signHash(
    signAlgo: number, hash: string, appendCert: boolean, asBase64String?: boolean): Promise<any> {
    store.commit(_act.SETTTEMP_MUT_ISSHOWMSG, false);
    try {
      let { certificate, privateKeyPassword } = this.signingParams.data;

      let { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();

      const signs = await cloudKeySignService.signHashes(certificate, privateKeyPassword, [hash], recaptchaToken, recaptchaType);

      await this.fillEndUserOwnerInfoFromSigns(signs, (s) => s);

      return signs && signs.length ? signs[0] : '';
    } finally {
      store.commit(_act.SETTTEMP_MUT_ISSHOWMSG, true);
    }
  }
}

export class SignTextWithQrCloudProviderStrategy extends SignTextCloudStrategy {
  protected getOwnCertificates(): Promise<any> {
    return this.euSignLib.GetOwnCertificates();
  }

  protected async signHash(
    signAlgo: number, hash: string, appendCert: boolean, asBase64String?: boolean): Promise<any> {
    try {
      return await executeCloudSignOperationWithConfirmationByQrCode(
        this.signingParams.signProvider,
        this.euSignLib,
        'SignHash',
        [signAlgo, hash, appendCert, asBase64String],
        this.signingParams.privateKeyType,
        AppSignKeyOperationType.Signing,
        this.mobileAppName
      );
    } catch (error) {
      if (isCancelledError(error)) {
        EventBus.$emit('app-sign-reset-private-key', {
          privateKeyType: this.signingParams?.privateKeyType
        });
      }
      throw error;
    }
  }
}

export class SignTextWithIdCloudProviderStrategy extends SignTextCloudStrategy {
  protected async getOwnCertificates(): Promise<any> {
    return this.euSignLib.GetOwnCertificates();
  }

  protected async signHash(
    signAlgo: number, hash: string, appendCert: boolean, asBase64String?: boolean): Promise<any> {
    setLoadingText({
      text: getLocale('appSignCloudProviderDefault.selectKeyInMobileApp', { mobileAppName: this.mobileAppName }).toString()
    });

    setLoadingCancelCallback(() => this.euSignLib.ResetOperationKSP());

    try {
      return await this.euSignLib.SignHash(signAlgo, hash, appendCert, asBase64String);
    } finally {
      setLoadingText({ text: '' });

      setLoadingCancelCallback(null);
    }
  }
}
